import {Card, Action, DataStatus} from 'platform/components';
import {Box, Show} from 'platform/foundation';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useListLatestVehiclesQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {useDataQuery, usePermissions} from '@dms/shared';

import {TestIdProps, composePath, useNavigate} from 'shared/web';

import {DashboardTileSize} from '../../types/DashboardTileSize';
import {LatestVehicleRows} from './components/LatestVehicleRows';

interface LatestVehiclesProps extends TestIdProps {
  branchId: string | undefined;
  size: DashboardTileSize;
}

export function LatestVehicles(props: LatestVehiclesProps) {
  const navigate = useNavigate();

  const [canReadVehicle] = usePermissions({
    permissionKeys: ['vehicleRead'],
    scopes: {
      vehicleRead: {
        // EP for latest vehicles handles participation scope
        participation: 'SKIP',
        // EP for latest vehicles handles branch scope
        branchId: 'SKIP',
      },
    },
  });

  const {
    data: latestVehicles,
    isLoading: isLatestVehiclesLoading,
    isError: isLatestVehiclesError,
  } = useListLatestVehiclesQuery(
    {
      branchId: props.branchId,
    },
    {refetchOnMountOrArgChange: true, skip: !canReadVehicle}
  );

  const {queryParams} = useDataQuery('vehicle', {
    order: [{columnKey: 'createdAt', order: 'DESC'}],
  });

  const onClickOpenLatestVehicles = () => {
    navigate(composePath(vehiclesRoutes.home, {queryParams}));
  };

  const displayLatestVehiclesAction: Action = {
    title: i18n.t('page.homepage.labels.view'),
    onClick: onClickOpenLatestVehicles,
    variant: 'link',
    type: 'button',
    rightIcon: 'navigation/chevron_right',
  };

  return (
    <Show when={canReadVehicle}>
      <Card
        title={i18n.t('page.homepage.labels.latestVehicles')}
        hasSeparator={false}
        actions={[displayLatestVehiclesAction]}
        isFullHeight
      >
        <Box minHeight={56} height="100%">
          <DataStatus
            minHeight="100%"
            isLoading={isLatestVehiclesLoading}
            isError={isLatestVehiclesError}
            isEmpty={isNilOrEmpty(latestVehicles)}
          >
            <LatestVehicleRows vehicles={latestVehicles ?? []} size={props.size} />
          </DataStatus>
        </Box>
      </Card>
    </Show>
  );
}
